import { WageFrequency, ScheduleType } from "@api/shared-runtime/enums";
import { TimeZone } from "@api/shared-runtime/timezone";
import { addYears, isAfter, isBefore, parseISO, startOfToday } from "date-fns";
import { z } from "zod";

// Re-export TimeZone for convenience
export { TimeZone };

/**
 * Detect the user's timezone, mapping to a supported TimeZone.
 * Falls back to America/New_York if not recognized.
 */
export function detectUserTimezone(): TimeZone {
  try {
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Check if it's one of our supported timezones
    if (Object.values(TimeZone).includes(userTz as TimeZone)) {
      return userTz as TimeZone;
    }
    // Default to Eastern if not recognized
    return TimeZone.AmericaNewYork;
  } catch {
    return TimeZone.AmericaNewYork;
  }
}

const today = startOfToday();
const maxDate = addYears(today, 1);

/**
 * Schema for a single schedule entry (for API submission).
 * - startsAt: ISO datetime string (e.g., "2024-12-05T22:00:00")
 * - durationMinutes: length of shift in minutes
 * - rrule: RFC 5545 RRULE string (e.g., "FREQ=WEEKLY;INTERVAL=2;BYDAY=MO,WE,FR") or null for single occurrence
 */
export const scheduleSchema = z.object({
  startsAt: z.string(), // ISO datetime
  durationMinutes: z.number().int().positive(),
  rrule: z.string().nullable(),
  scheduleType: z.nativeEnum(ScheduleType).default(ScheduleType.Regular),
});

export type Schedule = z.infer<typeof scheduleSchema>;

const jobOfferWageFrequencySchema = z.union([
  z.literal(WageFrequency.OneTime),
  z.literal(WageFrequency.Hourly),
]);

/** Whether an offer takes its wages from a wage set or holds its own. */
export enum OfferWageSource {
  Custom = "custom",
  OrganizationWageSet = "organization-wage-set",
}

const REGULAR_WAGE_PATTERN = /^\d+$/;

export const jobOfferDataSchema = z
  .object({
    facility: z.object({
      id: z.string(),
      name: z.string(),
      address1: z.string().nullable(),
      address2: z.string().nullable().optional(),
      city: z.string().nullable(),
      state: z.string().nullable(),
      zipCode: z.string().nullable(),
      isRemote: z.boolean(),
    }),
    // Required unless the offer uses a wage set; checked in the refine below.
    regularWage: z.string().optional(),
    // Undefined means custom wages.
    wageSource: z.nativeEnum(OfferWageSource).optional(),
    organizationWageSetId: z.string().nullable().optional(),
    regularOvertimeWage: z
      .string()
      .regex(/^\d*?$/, "Regular overtime wage must be a number")
      .nullable()
      .optional(),
    weekendWage: z
      .string()
      .regex(/^\d*?$/, "Weekend wage must be a number")
      .nullable()
      .optional(),
    weekendOvertimeWage: z
      .string()
      .regex(/^\d*?$/, "Weekend overtime wage must be a number")
      .nullable()
      .optional(),
    onCallWage: z
      .string()
      .regex(/^\d*?$/, "On-call wage must be a number")
      .nullable()
      .optional(),
    onCallOvertimeWage: z
      .string()
      .regex(/^\d*?$/, "On-call overtime wage must be a number")
      .nullable()
      .optional(),
    onCallCallbackWage: z
      .string()
      .regex(/^\d*?$/, "On-call callback wage must be a number")
      .nullable()
      .optional(),
    onCallCallbackOvertimeWage: z
      .string()
      .regex(/^\d*?$/, "On-call callback overtime wage must be a number")
      .nullable()
      .optional(),
    holidayWage: z
      .string()
      .regex(/^\d*?$/, "Holiday wage must be a number")
      .nullable()
      .optional(),
    holidayOvertimeWage: z
      .string()
      .regex(/^\d*?$/, "Holiday overtime wage must be a number")
      .nullable()
      .optional(),
    wageFrequencyId: jobOfferWageFrequencySchema,
    timezoneId: z.nativeEnum(TimeZone),
    description: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    schedules: z
      .array(scheduleSchema)
      .min(1, "At least one schedule is required"),
    providerPools: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
        }),
      )
      .nullable()
      .optional(),
    providerUsers: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
        }),
      )
      .optional(),
    decision: z
      .object({
        organizationDecision: z.boolean().nullable(),
        organizationDecisionAt: z.string().nullable(),
        providerDecision: z.boolean().nullable(),
        providerDecisionAt: z.string().nullable(),
        providerCanceledAt: z.string().nullable(),
        isMe: z.boolean(),
      })
      .nullable()
      .optional(),
    otherUserAccepted: z.boolean().nullable().optional(),
    canceledByOrganizationBusinessUnitAt: z.string().nullable().optional(),
    expiresInDuration: z.number().int().positive().nullable().optional(),
    expiresInDurationUnit: z
      .enum(["minutes", "hours", "days"])
      .nullable()
      .optional(),
  })
  .refine(
    (data) => {
      const hasProviderPools =
        data.providerPools && data.providerPools.length > 0;
      const hasProviderUsers =
        data.providerUsers && data.providerUsers.length > 0;
      return hasProviderPools || hasProviderUsers;
    },
    {
      message: "Please select at least one provider pool or provider user",
      path: ["providerPools"],
    },
  )
  .refine(
    (data) =>
      data.schedules?.every((schedule) => {
        if (!schedule.startsAt) {
          return false;
        }
        const start = parseISO(schedule.startsAt);
        return !isBefore(start, today) && !isAfter(start, maxDate);
      }) ?? false,
    {
      message:
        "Schedule start dates must be today or later and no more than one year from today.",
      path: ["schedules"],
    },
  )
  .superRefine((data, ctx) => {
    if (data.wageSource === OfferWageSource.OrganizationWageSet) {
      if (!data.organizationWageSetId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Select a wage set",
          path: ["organizationWageSetId"],
        });
      }
    } else if (!REGULAR_WAGE_PATTERN.test(data.regularWage ?? "")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Regular wage must be a number",
        path: ["regularWage"],
      });
    }
  })
  .superRefine((data, ctx) => {
    const hasN = data.expiresInDuration != null;
    const hasU = data.expiresInDurationUnit != null;
    if (hasN !== hasU) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Provide both a number and a unit, or leave both blank.",
        path: [hasN ? "expiresInDurationUnit" : "expiresInDuration"],
      });
    }
  });

export type JobOfferData = z.infer<typeof jobOfferDataSchema>;

/**
 * Helper to calculate duration in minutes from start/end time strings.
 * Handles overnight shifts (e.g., 10pm - 6am).
 */
export function calculateDurationMinutes(
  startTime: string,
  endTime: string,
): number {
  const [startHour, startMin] = startTime.split(":").map(Number);
  const [endHour, endMin] = endTime.split(":").map(Number);

  const startMinutes = startHour * 60 + startMin;
  let endMinutes = endHour * 60 + endMin;

  // If end is before or equal to start, it's overnight (add 24 hours)
  if (endMinutes <= startMinutes) {
    endMinutes += 24 * 60;
  }

  return endMinutes - startMinutes;
}

/**
 * Helper to format duration as hours and minutes string.
 */
export function formatDuration(durationMinutes: number): string {
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;
  if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}m`;
}
